import React, { useCallback, useEffect, useState } from 'react';
import {
  isConfigured,
  fetchParameters,
  fetchCategories,
  fetchConfigValues,
} from '../state/index.js';
import ParameterCard from './config/ParameterCard.jsx';
import ResolutionTester from './config/ResolutionTester.jsx';
import { FAMILY_META } from './config/constants.js';

const FAMILY_ORDER = ['strategic', 'set_once', 'internal'];

export default function ConfigTab() {
  const [parameters, setParameters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [configValues, setConfigValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reloadValues = useCallback(async () => {
    setConfigValues(await fetchConfigValues());
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [params, cats, values] = await Promise.all([
        fetchParameters(),
        fetchCategories(),
        fetchConfigValues(),
      ]);
      setParameters(params);
      setCategories(cats);
      setConfigValues(values);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isConfigured) loadAll();
    else setLoading(false);
  }, [loadAll]);

  if (!isConfigured) {
    return (
      <section className="panel">
        <h1>Configuration</h1>
        <div className="status status-warn">
          Supabase not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, and add
          `uee` to Dashboard → API → Exposed schemas.
        </div>
      </section>
    );
  }

  if (loading) return <section className="panel"><p className="muted">Loading configuration…</p></section>;
  if (error)
    return (
      <section className="panel">
        <div className="status status-warn">Could not load configuration: {error}</div>
        <button className="btn" onClick={loadAll}>Retry</button>
      </section>
    );

  const byFamily = (fam) => parameters.filter((p) => p.family === fam);

  return (
    <div className="config-tab">
      <ResolutionTester parameters={parameters} configValues={configValues} />

      {FAMILY_ORDER.map((fam) => {
        const meta = FAMILY_META[fam];
        const params = byFamily(fam);
        if (params.length === 0) return null;
        return (
          <section key={fam} className={fam === 'internal' ? 'family internal-family' : 'family'}>
            <div className="family-head">
              <h2>{meta.title}</h2>
              <span className={fam === 'internal' ? 'family-note warn' : 'family-note'}>{meta.note}</span>
            </div>
            <div className="param-grid">
              {params.map((p) => (
                <ParameterCard
                  key={p.id}
                  param={p}
                  categories={categories}
                  configValues={configValues}
                  onChanged={reloadValues}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
